import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {User} from '../../../shared/interface/user';
import {UserService} from '../../../shared/services/user.service';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {PostService} from "../../../shared/services/post.service";
import {HttpErrorResponse} from "@angular/common/http";
import {AddPermissionTagUserComponent} from  "../../asf360/permission-tags/add-permission-tag-user/add-permission-tag-user.component";
import {
  PermissionTagsUserImportComponent
} from "../../asf360/permission-tags/permission-tags-user-import/permission-tags-user-import.component";


@Component({
  selector: 'app-all-user-list',
  templateUrl: './all-user-list.component.html',
  styleUrls: ['./all-user-list.component.scss']
})
export class AllUserListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() active?: number = null;
  @Input() permissionTagId?: number = null;
  @Input() roleNot?: string = null;
  @Input() limitedView = null;
  @Input() showFilter = true;
  @Input() embeddedView = false;
  @Output() permissionTagChanged = new EventEmitter<any>();
  @Input() excludeColumns: string[] = null;
  @Input() onlyLicenses = null;
  @Input() employee = null;

  initialFilters: any;
  users: User[] = [];
  displayedColumns: string[] = ['licenseFullName', 'username', 'typ', 'active', 'firstName', 'lastName', 'phone', 'email', 'department', 'supervisor', 'delete' ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
  ) {
    if (this.limitedView) {
      this.userService.getEmployeesListSubject()
        .pipe(
          takeUntil(this.destroyed$),
          tap((e: CountedAndPaginated<User>) => {
            this.users = e.items;
            this.totalData = e.pagination.total;
          })
        ).subscribe();
    }else {
      this.userService.getUserListSubject()
        .pipe(
          takeUntil(this.destroyed$),
          tap((e: CountedAndPaginated<User>) => {
            this.users = e.items;
            this.totalData = e.pagination.total;
          })
        ).subscribe();
    }
    this.userPermissions = authService.getUserPermissions();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$username: 'string',
      contains$firstName: 'string',
      contains$lastName: 'string',
      contains$phone: 'string',
      contains$email: 'string',
      department: 'string',
      supervisor: 'string',
      active: 'int',
      role: 'string',
      employeeList360: 'int'
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.displayedColumns = data.displayedColumns ?? this.displayedColumns;
        this.active = data.active ?? this.active;
        this.limitedView = this.limitedView ?? data.limitedView ?? false;
        this.excludeColumns = this.excludeColumns ?? data.excludeColumns ?? [];
        this.permissionTagId = data.permissionTagId ?? this.permissionTagId;
        this.roleNot = this.roleNot ?? data.roleNot ?? '';
        this.onlyLicenses = this.onlyLicenses ?? data.onlyLicenses ?? null;
        this.employee = this.employee ?? data.employee ?? null;

        if (!this.onlyLicenses) {
          this.excludeColumns.push('licenseFullName');
        }
        this.displayedColumns = this.displayedColumns.filter((e: string) => this.excludeColumns.indexOf(e) === -1);

        if (!this.embeddedView) {
        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          });
        } else {
          this.loadListData();
        }
  });
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      if (this.embeddedView) {
        this.loadListData();
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      }
    });
  }
  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    if (this.limitedView) {
      this.userService.loadEmployeesList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
    }else{
      this.userService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
    }
  }

  downloadReport(): void {
    if (this.limitedView) {
      this.userService.downloadEmployeesReport(this.buildFilters());
    }else{
      this.userService.downloadReport(this.buildFilters());
    }
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.active) {
      filters.active = this.active;
    }
    if (null !== this.permissionTagId) {
      filters.permissionTag = this.permissionTagId;
    }
    if (null !== this.roleNot) {
      filters.roleNot =  this.roleNot;

    }

    if (this.employee !== null) {
      filters.employee = this.employee;
    }

    return filters;

  }

  delete(userId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć użytkownika z grupy?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userService.deletePermissionTag({permissionTag: this.permissionTagId} , userId)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto użytkownika z grupy', '', {
              duration: 2000,
            });
            this.loadListData();
          }, (error: HttpErrorResponse) => {
            this.snack.open('Podczas usuwania użytkownika wystąpił błąd', error.status.toString(), {
              duration: 2000,
            });


          })
        ;
      }
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(AddPermissionTagUserComponent, {
      width: '700px',
      data: {permissionTag: this.permissionTagId},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.permissionTagChanged.emit();
      this.loadListData();
    });
  }

  import(): void {
    const dialogRef = this.dialog.open(PermissionTagsUserImportComponent, {
      width: '700px',
      data: {permissionTag: this.permissionTagId},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.permissionTagChanged.emit();
      this.loadListData();
    });
  }
}
